import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, Nav, Tab } from 'react-bootstrap';
import { toast } from 'react-toastify';

const MediaModal = ({ show, onHide, onCapture, questionId }) => {
  const [activeTab, setActiveTab] = useState('upload');
  const [isRecording, setIsRecording] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  

  useEffect(() => {
    if (!show) {
      stopMediaStream();
    }
  }, [show]);

  // Initialize camera when tab changes or modal shows
  useEffect(() => {
    if (show && activeTab !== 'upload') {
      startMediaStream(activeTab);
    }
  }, [show, activeTab]);

  const stopMediaStream = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsRecording(false);
  };

  const startMediaStream = async (type) => {
    try {
      stopMediaStream();
      const constraints = {
        audio: type !== 'photo',
        video: type !== 'audio' ? { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } : false
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);

      if (videoRef.current && type !== 'audio') {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play().catch(err => {
          console.error('Error playing video:', err);
          toast.error('Failed to initialize camera preview');
        });
      }
    } catch (err) {
      console.error('Media stream error:', err);
      toast.error('Failed to access camera/microphone');
    }
  };

  const handleCapture = (type) => {
    if (type === 'photo' && videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
      canvas.toBlob((blob) => {
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        onCapture(file, file.name);
        onHide();
      }, 'image/jpeg');
    } else {
      // Start recording for video/audio
      if (!stream) return;
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: type === 'audio' ? 'audio/webm' : 'video/webm'
        });
        const file = new File([blob], `${type}_${Date.now()}.webm`, {
          type: type === 'audio' ? 'audio/webm' : 'video/webm'
        });
        onCapture(file, file.name);
        onHide();
      };

      mediaRecorder.start();
      setIsRecording(true);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="media-modal">
      <Modal.Header closeButton className="border-bottom">
        <Modal.Title className="h5 mb-0">Add Media</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <Tab.Container 
          activeKey={activeTab} 
          onSelect={(key) => {
            setActiveTab(key);
          }}
        >
          <Nav variant="tabs" className="nav-fill border-bottom">
            <Nav.Item>
              <Nav.Link eventKey="upload" className="rounded-0 border-0">
                <i className="fas fa-upload me-2"></i>Upload
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="photo" className="rounded-0 border-0">
                <i className="fas fa-camera me-2"></i>Photo
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="video" className="rounded-0 border-0">
                <i className="fas fa-video me-2"></i>Video
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="audio" className="rounded-0 border-0">
                <i className="fas fa-microphone me-2"></i>Audio
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey="upload" className="p-4">
              <div className="upload-zone p-4 text-center border-2 border-dashed rounded-3">
                <input
                  type="file"
                  accept="image/*,video/*,audio/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('File size must be less than 5MB');
                        return;
                      }
                      onCapture(file, file.name);
                      onHide();
                    }
                  }}
                  className="form-control form-control"
                />
                <p className="text-muted mt-2 mb-0">
                  <small>Maximum file size: 5MB</small>
                </p>
              </div>
            </Tab.Pane>

            <Tab.Pane eventKey="photo" className="p-0">
              <div className="camera-container bg-dark">
                <div className="video-wrapper">
                  {activeTab === 'photo' && (
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="camera-preview w-100 h-100"
                    />
                  )}
                </div>
                <div className="camera-controls p-3 bg-light">
                  <Button 
                    variant="primary" 
                    size="lg"
                    className="rounded-pill px-4"
                    onClick={() => handleCapture('photo')}
                  >
                    <i className="fas fa-camera me-2" />
                    Take Photo
                  </Button>
                </div>
              </div>
            </Tab.Pane>

            <Tab.Pane eventKey="video" className="p-0">
              <div className="video-container bg-dark">
                {activeTab === 'video' && (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-100"
                    style={{ maxHeight: '60vh', objectFit: 'cover' }}
                  />
                )}
                <div className="p-3 bg-light text-center">
                  <Button 
                    variant={isRecording ? "danger" : "primary"}
                    size="lg"
                    className="rounded-pill px-4"
                    onClick={() => {
                      if (isRecording) {
                        mediaRecorderRef.current?.stop();
                        setIsRecording(false);
                      } else {
                        handleCapture('video');
                      }
                    }}
                  >
                    <i className={`fas fa-${isRecording ? 'stop' : 'video'} me-2`} />
                    {isRecording ? 'Stop Recording' : 'Start Recording'}
                  </Button>
                </div>
              </div>
            </Tab.Pane>

            <Tab.Pane eventKey="audio" className="p-0">
              <div className="audio-preview text-center p-5 bg-light">
                <div className="mb-4">
                  <i className={`fas fa-microphone${isRecording ? '-slash' : ''} fa-3x ${isRecording ? 'text-danger' : 'text-primary'}`} />
                </div>
                <Button 
                  variant={isRecording ? "danger" : "primary"}
                  size="lg"
                  className="rounded-pill px-4"
                  onClick={() => {
                    if (isRecording) {
                      mediaRecorderRef.current?.stop();
                      setIsRecording(false);
                    } else {
                      handleCapture('audio');
                    }
                  }}
                >
                  <i className={`fas fa-${isRecording ? 'stop' : 'microphone'} me-2`} />
                  {isRecording ? 'Stop Recording' : 'Start Recording'}
                </Button>
              </div>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Modal.Body>

      <style jsx>{`
        .media-modal .modal-content {
          border-radius: 1rem;
          overflow: hidden;
        }
        .camera-container {
          position: relative;
          width: 100%;
          min-height: 60vh;
        }
        .video-wrapper {
          position: relative;
          width: 100%;
          height: calc(60vh - 80px);
        }
        .camera-preview {
          position: absolute;
          object-fit: cover;
          transform: scaleX(-1);
        }
        .border-dashed {
          border-style: dashed !important;
        }
        .nav-tabs .nav-link {
          color: #6c757d;
          transition: all 0.2s;
        }
        .nav-tabs .nav-link.active {
          color: #0d6efd;
          font-weight: 500;
        }
        @media (max-width: 576px) {
          .camera-container,
          .video-wrapper {
            min-height: 40vh;
          }
          .video-wrapper {
            height: calc(40vh - 80px);
          }
          .camera-controls {
            padding: 1rem !important;
          }
        }
      `}</style>
    </Modal>
  );
};

export default MediaModal;